import { Grid, Typography } from '@mui/material';
import React from 'react';
import { CommunicationTrackingStatus } from '../../../../../hooks/useCommunicationTracking';
import { CommunicationTrackingEventType } from '../../../../../api';

type CommunicationTrackingSummaryProps = {
  status: CommunicationTrackingStatus;
  loading: boolean;
};
const getEmailStatus = (emailEvents: any[]) => {
  if (emailEvents.length === 0) return null;

  const failed = emailEvents.some(
    (e) =>
      e.event_type === CommunicationTrackingEventType.EMAIL_BOUNCE ||
      e.event_type === CommunicationTrackingEventType.EMAIL_DROPPED ||
      e.event_type === CommunicationTrackingEventType.EMAIL_SPAM_REPORT,
  );
  const opened = emailEvents.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_OPEN);
  const delivered = emailEvents.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_DELIVERED);
  const processed = emailEvents.some((e) => e.event_type === CommunicationTrackingEventType.EMAIL_PROCESSED);

  if (failed) return { checkmarks: '✗', color: '#f44336' };
  if (opened) return { checkmarks: '✓✓', color: '#2196f3' };
  if (delivered) return { checkmarks: '✓✓', color: '#999' };
  if (processed) return { checkmarks: '✓', color: '#999' };
  return null;
};

const getSmsStatus = (smsEvents: any[]) => {
  if (smsEvents.length === 0) return null;

  const failed = smsEvents.some(
    (e) =>
      e.event_type === CommunicationTrackingEventType.SMS_FAILED ||
      e.event_type === CommunicationTrackingEventType.SMS_UNDELIVERED ||
      e.event_type === CommunicationTrackingEventType.SMS_WRONG_NUMBER ||
      e.event_type === CommunicationTrackingEventType.SMS_GENERIC_ERROR,
  );
  const delivered = smsEvents.some((e) => e.event_type === CommunicationTrackingEventType.SMS_DELIVERED);
  const sent = smsEvents.some(
    (e) =>
      e.event_type === CommunicationTrackingEventType.SMS_SENT ||
      e.event_type === CommunicationTrackingEventType.SMS_DISPATCHED,
  );

  if (failed) return { checkmarks: '✗', color: '#f44336' };
  if (delivered) return { checkmarks: '✓✓', color: '#2196f3' };
  if (sent) return { checkmarks: '✓', color: '#999' };
  return null;
};
export const CommunicationTrackingSummary = ({ status, loading }: CommunicationTrackingSummaryProps) => {
  const getCommunicationStats = () => {
    if (loading || status.events.length === 0) {
      return null;
    }

    const emailEvents = status.events.filter((e) => e.event_type?.startsWith('email_'));
    const smsEvents = status.events.filter((e) => e.event_type?.startsWith('sms_'));

    const emailIds = new Set(emailEvents.map((e) => e.sendgrid_email_data?.sg_message_id).filter(Boolean));
    const totalEmails = emailIds.size;

    // For SMS, show status if there are any SMS events (don't rely on message_sid count)
    const hasSmsEvents = smsEvents.length > 0;

    if (totalEmails === 0 && !hasSmsEvents) {
      return null;
    }

    const emailStatus = getEmailStatus(emailEvents);
    const smsStatus = getSmsStatus(smsEvents);

    return { emailStatus, smsStatus, totalEmails, hasSmsEvents };
  };

  const stats = getCommunicationStats();

  if (!stats) {
    return null;
  }

  return (
    <Grid container alignItems="center" gap={2}>
      {stats.emailStatus && stats.totalEmails > 0 && (
        <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto' }}>
          <Typography variant="body2">📧</Typography>
          <Typography variant="body2" sx={{ color: stats.emailStatus.color, fontWeight: 'bold', fontSize: '16px' }}>
            {stats.emailStatus.checkmarks}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats.totalEmails === 1 ? '1' : stats.totalEmails}
          </Typography>
        </Grid>
      )}

      {stats.smsStatus && stats.hasSmsEvents && (
        <Grid container alignItems="center" gap={0.5} sx={{ width: 'auto' }}>
          <Typography variant="body2">📱</Typography>
          <Typography variant="body2" sx={{ color: stats.smsStatus.color, fontWeight: 'bold', fontSize: '16px' }}>
            {stats.smsStatus.checkmarks}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
